import { GQL } from '@aline/graphql';
import Color from 'color';
import type { IBorderOptions, IParagraphOptions, IParagraphPropertiesOptions } from 'docx';
import { AlignmentType, BorderStyle, PageBreak, Paragraph } from 'docx';
import type { NodeEntry } from 'slate';
import { Element, Text } from 'slate';

import { createBookmarkMarker } from './bookmark';
import {
  createChangeAttributes,
  createCheckboxField,
  createCrossReference,
  createDocxDrawing,
  createHyperlink,
  createInlineInstructionField,
  createInputField,
  createRunProperties,
  createTextRun,
} from './internal';
import { createTab, createTabStops } from './tab';
import type { ChangeAttributeProperties, DocxOptionsWithTableSuggestions } from './types';
import {
  isAlineInlineElement,
  isCrossReferenceElement,
  isInlineInstructionFieldElement,
  Queries,
} from '../../../slate-api';
import type {
  ParagraphBorders,
  ParagraphChildElement,
  ParagraphElement,
  ParagraphProps,
  TableBorder,
  TableBorderStyle,
  TabOptions,
} from '../../../slate-api';
import { inToTwips, lineSpacingToTwips, ptToEighthPts, ptToTwips } from '../../../util';
import { toHex } from '../../../util/color';

type ParagraphChildren = Exclude<IParagraphOptions['children'], undefined>;

export function createDocxParagraph(
  paragraphEntry: NodeEntry<ParagraphElement>,
  options: DocxOptionsWithTableSuggestions
): Paragraph {
  const [paragraph, paragraphPath] = paragraphEntry;
  const tab = Queries.getFirstNotPositionalTabInDescendants(paragraph);
  const paragraphProperties = createParagraphProperties(paragraph, options, tab?.options);

  return new Paragraph({
    ...paragraphProperties,
    children: paragraph.children.flatMap((child, index) =>
      createParagraphChild([child, paragraphPath.concat(index)], options)
    ),
  });
}

export function createParagraphChild(
  childEntry: NodeEntry<ParagraphChildElement>,
  options: DocxOptionsWithTableSuggestions
): ParagraphChildren {
  const [child, childPath] = childEntry;

  if (!Element.isElement(child)) return createTextRun([child, childPath], options);

  switch (child.type) {
    case 'link':
      return [createHyperlink([child, childPath], options)];
    case 'INPUT':
      if (child.variant === GQL.FieldVariant.Checkbox) {
        return child.children.flatMap((optionChild, optionIndex) => {
          if (!Text.isText(optionChild) && !isAlineInlineElement(optionChild)) return [];
          return createParagraphChild([optionChild, childPath.concat(optionIndex)], options);
        });
      }
      return createInputField([child, childPath], options);
    case 'break':
      return [new PageBreak()];
    case 'drawing': {
      const drawing = createDocxDrawing([child, childPath], options);
      return drawing ? [drawing] : [];
    }
    case 'dynamic-inline':
      throw new Error('Dynamic inline elements are not supported yet.');
    case 'instructionField': {
      if (isCrossReferenceElement(child)) {
        return createCrossReference([child, childPath], options);
      }
      if (isInlineInstructionFieldElement(child)) {
        return createInlineInstructionField([child, childPath], options);
      }
      throw new Error('Block instruction field elements are not supported yet.');
    }
    case 'tab':
      return [createTab([child, childPath], options)];

    case 'bookmarkStart':
    case 'bookmarkEnd':
      return [createBookmarkMarker([child, childPath])];
    case 'suggestionMarkerStart':
    case 'suggestionMarkerEnd':
      // Suggestion markers are internal-only and not serialized to docx
      return [];
    case 'CANNED':
      return child.children.flatMap((textChild, i) =>
        createTextRun([textChild, childPath.concat(i)], options)
      );
    case 'checkbox':
      return createCheckboxField([child, childPath]);
  }
}

function createParagraphProperties(
  paragraphProps: Partial<ParagraphProps>,
  options: DocxOptionsWithTableSuggestions,
  tabOptions?: TabOptions
): IParagraphPropertiesOptions {
  const {
    alignment,
    borders,
    contextualSpacing,
    glyphProps,
    indentationFirstLine,
    indentationLeft,
    indentationRight,
    lineSpacing,
    listProps,
    shade,
    spacingAfter,
    spacingBefore,
    variant,
  } = paragraphProps;

  const glyphSuggestedEdits = glyphProps?.suggestedEdits ?? [];
  let paragraphMarkInsertion: ChangeAttributeProperties | undefined;
  let paragraphMarkDeletion: ChangeAttributeProperties | undefined;
  for (const edit of glyphSuggestedEdits) {
    if (edit.type === 'insertParagraph' && !paragraphMarkInsertion) {
      paragraphMarkInsertion = createChangeAttributes(options.context, edit);
    }
    if (edit.type === 'deleteParagraph' && !paragraphMarkDeletion) {
      paragraphMarkDeletion = createChangeAttributes(options.context, edit);
    }
  }

  const runProperties = {
    ...createRunProperties(glyphProps ?? {}),
    deletion: paragraphMarkDeletion ?? options.tableSuggestions?.deletion,
    insertion: paragraphMarkInsertion ?? options.tableSuggestions?.insertion,
  };

  const properties: IParagraphPropertiesOptions = {
    alignment: alignment ? getAlignment(alignment) : undefined,
    border: borders ? convertParagraphBordersToDocx(borders) : undefined,
    contextualSpacing,
    // Indent values are in twips per http://officeopenxml.com/WPindentation.php.
    indent: {
      firstLine:
        typeof indentationFirstLine === 'number' && indentationFirstLine >= 0
          ? inToTwips(indentationFirstLine)
          : undefined,
      hanging:
        typeof indentationFirstLine === 'number' && indentationFirstLine < 0
          ? inToTwips(Math.abs(indentationFirstLine))
          : undefined,
      left: typeof indentationLeft === 'number' ? inToTwips(indentationLeft) : undefined,
      right: typeof indentationRight === 'number' ? inToTwips(indentationRight) : undefined,
    },
    run: Object.keys(runProperties).length ? runProperties : undefined,
    shading: shade?.fillColor ? { fill: toHex(shade.fillColor) } : undefined,
    // Spacing values are in twips per http://officeopenxml.com/WPspacing.php.
    spacing: {
      after: typeof spacingAfter === 'number' ? ptToTwips(spacingAfter) : undefined,
      before: typeof spacingBefore === 'number' ? ptToTwips(spacingBefore) : undefined,
      line: typeof lineSpacing === 'number' ? lineSpacingToTwips(lineSpacing) : undefined,
    },
    style: variant,
    tabStops: tabOptions ? createTabStops(tabOptions) : undefined,
  };

  if (listProps && options.context.listDefinitions[listProps.listId]) {
    return {
      ...properties,
      numbering: { level: listProps.listLevel, reference: listProps.listId },
    };
  }

  return properties;
}

function getAlignment(
  alignment: ParagraphElement['alignment']
): NonNullable<IParagraphPropertiesOptions['alignment']> {
  switch (alignment) {
    case 'left':
      return AlignmentType.LEFT;
    case 'center':
      return AlignmentType.CENTER;
    case 'right':
      return AlignmentType.RIGHT;
    case 'justify':
      return AlignmentType.JUSTIFIED;
  }
}

type MutableParagraphBorders = {
  -readonly [K in keyof NonNullable<IParagraphPropertiesOptions['border']>]: NonNullable<
    IParagraphPropertiesOptions['border']
  >[K];
};

function convertParagraphBordersToDocx(
  borders: ParagraphBorders
): NonNullable<IParagraphPropertiesOptions['border']> {
  const result: MutableParagraphBorders = {};

  for (const side of ['top', 'bottom', 'left', 'right', 'between'] as const) {
    const partialBorder = borders[side];
    if (!partialBorder) continue;

    result[side] = convertParagraphBorderToDocx(Queries.mergeTableBorderWithDefault(partialBorder));
  }

  return result;
}

function convertParagraphBorderToDocx(border: TableBorder): IBorderOptions {
  return {
    size: ptToEighthPts(border.size),
    space: border.space ?? 0,
    style: alineParagraphBorderStyleToDocxStyle(border.size, border.style),
    ...createParagraphBorderColor(border),
  };
}

function createParagraphBorderColor(border: TableBorder): Pick<IBorderOptions, 'color'> {
  if (!border.color) return {};

  try {
    return { color: Color(border.color).hex() };
  } catch {
    return {};
  }
}

function alineParagraphBorderStyleToDocxStyle(
  size: number,
  alineStyle: TableBorderStyle
): IBorderOptions['style'] {
  if (!size) return BorderStyle.NIL;

  switch (alineStyle) {
    case 'dashed':
      return BorderStyle.DASHED;
    case 'dotted':
      return BorderStyle.DOTTED;
    case 'double':
      return BorderStyle.DOUBLE;
    case 'groove':
      return BorderStyle.WAVE;
    case 'hidden':
      return BorderStyle.NIL;
    case 'inset':
      return BorderStyle.INSET;
    case 'none':
      return BorderStyle.NIL;
    case 'outset':
      return BorderStyle.OUTSET;
    case 'ridge':
      return BorderStyle.WAVE;
    case 'solid':
    default:
      return BorderStyle.SINGLE;
  }
}
